import { FC, useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './card-ready.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import Avatar from '@components/molecules/avatar'
import Button from '@components/atoms/button'
import AddToWalletButton from '@components/atoms/add-to-wallet-button'
import Card from '@components/atoms/card'
import Divider from '@components/atoms/divider'
import GroupCard from '@components/organisims/group-card'
import PaymentMethodBrand from '@components/atoms/payment-method-brand'
import StepTracker from '@components/organisims/step-tracker'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { priceFormatter } from '@utils/number-helper'
import { isNonEmptyString } from '@utils/type-checkers'
import { getGroupStatus } from '@utils/temp-helpers'
import { useGroupLoaderData } from '../_hooks'

const CardReadyScreen: FC = () => {
    const { isSignedIn, isLoaded, user } = useUser()
    const group = useGroupLoaderData()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [isCardReady, setIsCardReady] = useState(false)
    const { groupStatus } = getGroupStatus(group)
    const isOrganizer = isSignedIn ? group.organizer?.id === user?.id : false
    const isMerchant = !!user?.publicMetadata.merchant && !!group.merchant && user?.publicMetadata.merchant?.id === group.merchant?.id
    const canView = group.type === 'merchant' ? isMerchant : isOrganizer

    const { userPledges } = useMemo(() => [...(group.pledges || [])].reduce<{ userPledges: Pledge[] }>((prev, pledge) => {
        if (isSignedIn && isNonEmptyString(user?.id)) {
            if (pledge.user?.id === user.id) return { ...prev, userPledges: [...prev.userPledges, pledge] };
            else return { ...prev }
        } else return prev
    }, { userPledges: [] }), [isSignedIn, user?.id, group.pledges])

    const userPledge = userPledges[0]
    const fromRecreate = searchParams.get('fromRecreate') === 'true'

    function handleNext() {
        navigate(fromRecreate ? `/group/${group.id}?fromRecreate=true` : `/group/${group.id}/invite`)
    }

    const isPhysicalCard = group.card?.cardType === 'physical'
    const isLinkedVirtualCard = group.card?.id === user?.publicMetadata.tera?.virtualCard?.id
    const isLinkedCard = isPhysicalCard || isLinkedVirtualCard

    if (!isLoaded) return null
    if (!canView || groupStatus !== 'active') return <Navigate to={`/group/${group.id}`} replace={true} />
    else return (<>
        <Column.Content className='group-banner-section' style={{ padding: '0rem' }}>
            <StepTracker currentStep={4} steps={3} />
        </Column.Content>
        <Column.Content className="card-ready-screen">
            <div className="title-section">
                {(!isLinkedCard && !isCardReady) ? (<>
                    <h2>Your Card is Being Created...</h2>
                </>) : (<>
                    <div className="bubble-md flex items-center justify-center" style={{ alignSelf: 'center', background: 'var(--splitcard-green-600)', boxShadow: 'var(--box-shadow)' }}>
                        <Icons.CheckSmall tint="var(--splitcard-white)" width="5em" height="5em" />
                    </div>
                    <h2 style={{ color: 'var(--splitcard-green-600)' }}>Your Card is Ready!</h2>
                </>)}
            </div>

            <GroupCard
                group={group}
                userPledges={userPledges}
                onCardReady={() => setIsCardReady(true)}
                hideAddMoney
            />

            <AddToWalletButton
                cardId={group.card?.id}
                lastFour={group.card?.lastFour}
                returnUrl={`/group/${group.id}/card-ready`}
                disabled={!isCardReady}
            />

            {isCardReady && <div className="info-banner">
                <div className="bubble-sml flex items-center justify-center" style={{ background: 'var(--splitcard-green-200)' }}>
                    <Icons.CheckSmall tint="var(--splitcard-green-600)" width="1.5rem" height="1.5rem" />
                </div>
                <div className='flex flex-col' style={{ width: '75%' }}>
                    <p className='text-body'><strong>You're all set!</strong></p>
                    <p className='text-body-2'>You can now start inviting others and spend with your group card.</p>
                </div>
            </div>}

            <Card>
                <div className="flex flex-col gap-4">
                    <p className="text-body"><strong>{group.title}</strong></p>
                    <div className="flex flex-row items-center" style={{ gap: 'inherit' }}>
                        <Avatar user={user} style={{ height: '2.4rem', minWidth: '2.4rem' }} />
                        <div className="flex flex-col items-stretch flex-1">
                            <p className="text-body-2 text-gray-500 text-left">Your contribution</p>
                            <p className='text-body'><strong>{priceFormatter.format((userPledge?.amount || 0) / 100)}</strong></p>
                        </div>
                    </div>
                    {(() => {
                        const creditAmount = userPledge?.credit?.amount || 0
                        const pledgeAmount = userPledge?.amount || 0
                        const isFullyFunded = creditAmount === pledgeAmount
                        const paymentMethod = userPledge?.paymentMethod
                        const hasPaymentMethod = !!paymentMethod

                        if (creditAmount === 0 && !hasPaymentMethod) return null

                        return (
                            <>
                                <Divider />
                                <p className="text-body"><strong>Your payment Info</strong></p>

                                {creditAmount > 0 && (
                                    <div className='flex flex-row justify-between'>
                                        <div className="flex flex-row items-center gap-4" style={{ marginBottom: (!isFullyFunded && hasPaymentMethod) ? '0.75rem' : 0 }}>
                                            <PaymentMethodBrand
                                                brand="splitcard-credit"
                                                height='2rem' width='2rem' style={{ height: '2rem' }}
                                            />
                                            <div className="flex flex-col items-stretch flex-1">
                                                <p className='text-body text-green-600'><strong>Splitcard Credit</strong></p>
                                            </div>
                                        </div>
                                        <p className='text-body text-green-600'><strong>{priceFormatter.format(creditAmount / 100)}</strong></p>
                                    </div>
                                )}

                                {(!isFullyFunded && paymentMethod) && (
                                    <div className='flex flex-row justify-between'>
                                        <div className="flex flex-row items-center gap-4">
                                            <PaymentMethodBrand
                                                brand={paymentMethod.type === 'credit_card'
                                                    ? paymentMethod.cardType || paymentMethod.type
                                                    : paymentMethod.type
                                                }
                                                height='2rem' width='2rem' style={{ height: '2rem' }}
                                            />
                                            <div className="flex flex-col items-stretch flex-1">
                                                <p className='text-body'><strong>{
                                                    paymentMethod.type === 'credit_card'
                                                        ? `Card ending in ${paymentMethod.lastFour}`
                                                        : paymentMethod.type.replace('_', ' ')
                                                }</strong></p>
                                            </div>
                                        </div>
                                        <p className='text-body'><strong>{priceFormatter.format((pledgeAmount - creditAmount) / 100)}</strong></p>
                                    </div>
                                )}
                            </>
                        )
                    })()}
                </div>
            </Card>

            <Column.Footer>
                <Button onClick={handleNext}>Next</Button>
            </Column.Footer>
        </Column.Content>
    </>)
}

const CardReadyRoute: RouteObject = {
    path: "card-ready",
    element: <CardReadyScreen />,
}

export default CardReadyRoute
